/**
 * Freehand Drawing Tool - Geometry Utilities
 * Vector math, transformations, and geometric calculations
 */

// ============================================================================
// Vector Math
// ============================================================================

export const vec2 = {
  create: (x = 0, y = 0) => ({ x, y }),
  
  clone: (v) => ({ x: v.x, y: v.y }),
  
  add: (a, b) => ({ x: a.x + b.x, y: a.y + b.y }),
  
  subtract: (a, b) => {
    if (!a || !b || typeof a.x === 'undefined' || typeof a.y === 'undefined' ||
        typeof b.x === 'undefined' || typeof b.y === 'undefined') {
      console.warn('Invalid vectors in subtract:', a, b);
      return { x: 0, y: 0 };
    }
    return { x: a.x - b.x, y: a.y - b.y };
  },
  
  multiply: (v, scalar) => ({ x: v.x * scalar, y: v.y * scalar }),
  
  divide: (v, scalar) => ({ x: v.x / scalar, y: v.y / scalar }),
  
  dot: (a, b) => a.x * b.x + a.y * b.y,
  
  cross: (a, b) => a.x * b.y - a.y * b.x,
  
  length: (v) => Math.sqrt(v.x * v.x + v.y * v.y),
  
  lengthSquared: (v) => v.x * v.x + v.y * v.y,
  
  distance: (a, b) => {
    if (!a || !b || typeof a.x === 'undefined' || typeof a.y === 'undefined' ||
        typeof b.x === 'undefined' || typeof b.y === 'undefined') {
      console.warn('Invalid vectors in distance:', a, b);
      return 0;
    }
    return vec2.length(vec2.subtract(a, b));
  },
  
  distanceSquared: (a, b) => vec2.lengthSquared(vec2.subtract(a, b)),
  
  normalize: (v) => {
    const len = vec2.length(v);
    return len > 0 ? vec2.divide(v, len) : vec2.create();
  },
  
  angle: (v) => Math.atan2(v.y, v.x),
  
  angleBetween: (a, b) => {
    const dot = vec2.dot(vec2.normalize(a), vec2.normalize(b));
    return Math.acos(Math.max(-1, Math.min(1, dot)));
  },
  
  rotate: (v, angle) => {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return {
      x: v.x * cos - v.y * sin,
      y: v.x * sin + v.y * cos
    };
  },
  
  lerp: (a, b, t) => ({
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t
  }),
  
  perpendicular: (v) => ({ x: -v.y, y: v.x }),
  
  equals: (a, b, epsilon = 1e-6) => 
    Math.abs(a.x - b.x) < epsilon && Math.abs(a.y - b.y) < epsilon
};

// ============================================================================
// Angle and Snapping Utilities
// ============================================================================

export function snapAngle(from, to, stepDegrees = 45) {
  const diff = vec2.subtract(to, from);
  const currentAngle = vec2.angle(diff);
  const stepRadians = (stepDegrees * Math.PI) / 180;
  const snappedAngle = Math.round(currentAngle / stepRadians) * stepRadians;
  const length = vec2.length(diff);
  
  return vec2.add(from, {
    x: Math.cos(snappedAngle) * length,
    y: Math.sin(snappedAngle) * length
  });
}

export function snapToOrthogonal(from, to) {
  const diff = vec2.subtract(to, from);
  const absX = Math.abs(diff.x);
  const absY = Math.abs(diff.y);
  
  if (absX > absY) {
    return { x: to.x, y: from.y }; // horizontal
  } else {
    return { x: from.x, y: to.y }; // vertical
  }
}

export function snapToGrid(point, gridSize) {
  return {
    x: Math.round(point.x / gridSize) * gridSize,
    y: Math.round(point.y / gridSize) * gridSize
  };
}

export function degreesToRadians(degrees) {
  return (degrees * Math.PI) / 180;
}

export function radiansToDegrees(radians) {
  return (radians * 180) / Math.PI;
}

// ============================================================================
// Curve and Bezier Utilities
// ============================================================================

export function evaluateCubicBezier(p0, p1, p2, p3, t) {
  const u = 1 - t;
  const tt = t * t;
  const uu = u * u;
  const uuu = uu * u;
  const ttt = tt * t;
  
  return {
    x: uuu * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + ttt * p3.x,
    y: uuu * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + ttt * p3.y
  };
}

export function evaluateCubicBezierDerivative(p0, p1, p2, p3, t) {
  const u = 1 - t;
  const tt = t * t;
  const uu = u * u;
  
  return {
    x: 3 * uu * (p1.x - p0.x) + 6 * u * t * (p2.x - p1.x) + 3 * tt * (p3.x - p2.x),
    y: 3 * uu * (p1.y - p0.y) + 6 * u * t * (p2.y - p1.y) + 3 * tt * (p3.y - p2.y)
  };
}

export function getCubicBezierLength(p0, p1, p2, p3, steps = 100) {
  let length = 0;
  let prevPoint = p0;
  
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const point = evaluateCubicBezier(p0, p1, p2, p3, t);
    length += vec2.distance(prevPoint, point);
    prevPoint = point;
  }
  
  return length;
}

// ============================================================================
// Bounding Box and Hit Testing
// ============================================================================

export function getBoundingBox(points) {
  if (points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }
  
  let minX = points[0].x;
  let minY = points[0].y;
  let maxX = points[0].x;
  let maxY = points[0].y;
  
  for (let i = 1; i < points.length; i++) {
    const p = points[i];
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}

export function expandRect(rect, margin) {
  return {
    x: rect.x - margin,
    y: rect.y - margin,
    width: rect.width + 2 * margin,
    height: rect.height + 2 * margin
  };
}

export function pointInRect(point, rect) {
  return point.x >= rect.x && 
         point.x <= rect.x + rect.width &&
         point.y >= rect.y && 
         point.y <= rect.y + rect.height;
}

// ============================================================================
// Path Utilities
// ============================================================================

export function getPathBounds(nodes) {
  const points = nodes.map(node => node.p);
  // Also include handle points for accurate bounds
  nodes.forEach(node => {
    if (node.h1) points.push(node.h1);
    if (node.h2) points.push(node.h2);
  });
  return getBoundingBox(points);
}

export function isPathClosed(nodes, threshold = 5) {
  if (nodes.length < 3) return false;
  return vec2.distance(nodes[0].p, nodes[nodes.length - 1].p) <= threshold;
}

// ============================================================================
// Coordinate Transformations
// ============================================================================

export function applyTransform(point, transform) {
  // Apply scale
  let result = vec2.multiply(point, transform.scale.x); // assuming uniform scale for simplicity
  
  // Apply rotation
  if (transform.rotation !== 0) {
    result = vec2.rotate(result, transform.rotation);
  }
  
  // Apply translation
  result = vec2.add(result, transform.translate);
  
  return result;
}

export function screenToWorld(screenPoint, viewTransform) {
  // Reverse the view transform
  let worldPoint = vec2.subtract(screenPoint, viewTransform.translate);
  
  if (viewTransform.rotation !== 0) {
    worldPoint = vec2.rotate(worldPoint, -viewTransform.rotation);
  }
  
  worldPoint = vec2.divide(worldPoint, viewTransform.scale.x);
  
  return worldPoint;
}

export function worldToScreen(worldPoint, viewTransform) {
  return applyTransform(worldPoint, viewTransform);
}
